import { useCallback, useRef, useState } from "react";
import { StreamingAudioChunk } from "pages/media/components/storyboard/editor/types";

type StreamingAudioState = {
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  isComplete: boolean;
};

// PCM format: 16-bit signed little-endian mono @ 44100 Hz
const PCM_SAMPLE_RATE = 44100;

export const useStreamingAudio = (onPlaybackComplete?: () => void) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodesRef = useRef<AudioBufferSourceNode[]>([]);
  const nextPlayTimeRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const queryIdRef = useRef<string | null>(null);
  const pendingChunksRef = useRef<Map<number, Float32Array<ArrayBuffer>>>(
    new Map()
  );
  const lastProcessedChunkRef = useRef<number>(-1);
  const isCompleteRef = useRef<boolean>(false);
  const userPausedRef = useRef<boolean>(false);

  const [state, setState] = useState<StreamingAudioState>({
    isPlaying: false,
    isPaused: false,
    isLoading: false,
    isComplete: false,
  });

  const base64ToArrayBuffer = useCallback((base64: string): ArrayBuffer => {
    const binaryString = atob(base64);
    const buffer = new ArrayBuffer(binaryString.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return buffer;
  }, []);

  const pcmToFloat32 = useCallback(
    (buffer: ArrayBuffer): Float32Array<ArrayBuffer> => {
      const dataView = new DataView(buffer);
      const numSamples = buffer.byteLength / 2;
      const float32 = new Float32Array(numSamples);

      for (let i = 0; i < numSamples; i++) {
        const sample = dataView.getInt16(i * 2, true);
        float32[i] = sample / 32768;
      }

      return float32;
    },
    []
  );

  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      // Safari fallback
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContextClass();
    }
    return audioContextRef.current;
  }, []);

  const stop = useCallback(() => {
    sourceNodesRef.current.forEach((source) => {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // Already stopped
      }
    });
    sourceNodesRef.current = [];
    isPlayingRef.current = false;
    userPausedRef.current = false;
    setState((prev) => ({ ...prev, isPlaying: false, isPaused: false }));
  }, []);

  const reset = useCallback(() => {
    stop();
    pendingChunksRef.current.clear();
    lastProcessedChunkRef.current = -1;
    nextPlayTimeRef.current = 0;
    queryIdRef.current = null;
    isCompleteRef.current = false;
    userPausedRef.current = false;
    setState({
      isPlaying: false,
      isPaused: false,
      isLoading: false,
      isComplete: false,
    });
  }, [stop]);

  const playChunk = useCallback(
    (chunkIndex: number, samples: Float32Array<ArrayBuffer>) => {
      const audioContext = getAudioContext();

      const audioBuffer = audioContext.createBuffer(
        1,
        samples.length,
        PCM_SAMPLE_RATE
      );
      audioBuffer.copyToChannel(samples, 0);

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      const startTime = Math.max(
        audioContext.currentTime,
        nextPlayTimeRef.current
      );
      source.start(startTime);
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      sourceNodesRef.current.push(source);
      lastProcessedChunkRef.current = chunkIndex;

      setState((prev) => ({ ...prev, isPlaying: true }));

      source.onended = () => {
        const index = sourceNodesRef.current.indexOf(source);
        if (index > -1) {
          sourceNodesRef.current.splice(index, 1);
        }
        if (sourceNodesRef.current.length === 0 && isCompleteRef.current) {
          isPlayingRef.current = false;
          setState((prev) => ({ ...prev, isPlaying: false }));
          onPlaybackComplete?.();
        }
      };
    },
    [getAudioContext, onPlaybackComplete]
  );

  const processPendingChunks = useCallback(() => {
    const sortedIndices = [...pendingChunksRef.current.keys()].sort(
      (a, b) => a - b
    );

    for (const chunkIndex of sortedIndices) {
      const nextExpected = lastProcessedChunkRef.current + 1;

      if (chunkIndex === nextExpected || lastProcessedChunkRef.current === -1) {
        const samples = pendingChunksRef.current.get(chunkIndex);
        if (samples) {
          pendingChunksRef.current.delete(chunkIndex);
          playChunk(chunkIndex, samples);
        }
      } else if (chunkIndex < nextExpected) {
        pendingChunksRef.current.delete(chunkIndex);
      } else {
        break;
      }
    }
  }, [playChunk]);

  const pause = useCallback(async () => {
    const audioContext = audioContextRef.current;
    if (audioContext && audioContext.state === "running") {
      userPausedRef.current = true;
      setState((prev) => ({ ...prev, isPlaying: false, isPaused: true }));
      await audioContext.suspend();
    }
  }, []);

  const resume = useCallback(async () => {
    const audioContext = audioContextRef.current;
    if (audioContext && audioContext.state === "suspended") {
      userPausedRef.current = false;
      setState((prev) => ({ ...prev, isPlaying: true, isPaused: false }));
      await audioContext.resume();
      // Process any chunks that arrived while paused
      processPendingChunks();
    }
  }, [processPendingChunks]);

  const addChunk = useCallback(
    async (chunk: StreamingAudioChunk) => {
      // Reset if new query
      if (queryIdRef.current && queryIdRef.current !== chunk.query_id) {
        reset();
      }
      queryIdRef.current = chunk.query_id;

      if (chunk.is_final) {
        isCompleteRef.current = true;
      }

      setState((prev) => ({
        ...prev,
        isLoading: !chunk.is_final,
        isComplete: chunk.is_final,
      }));

      // If this is the final chunk and all audio has already finished playing,
      // trigger completion now (handles case where is_final arrives after audio ends)
      if (
        chunk.is_final &&
        sourceNodesRef.current.length === 0 &&
        lastProcessedChunkRef.current >= 0
      ) {
        isPlayingRef.current = false;
        setState((prev) => ({ ...prev, isPlaying: false }));
        onPlaybackComplete?.();
        return;
      }

      // Skip decoding if speech is empty (final chunk may have no audio data)
      if (!chunk.speech) {
        console.debug("[StreamingAudio] Skipping chunk with empty speech");
        return;
      }

      const arrayBuffer = base64ToArrayBuffer(chunk.speech);
      const samples = pcmToFloat32(arrayBuffer);
      pendingChunksRef.current.set(chunk.chunk_index, samples);

      const audioContext = getAudioContext();

      // Only resume if user didn't explicitly pause
      if (audioContext.state === "suspended" && !userPausedRef.current) {
        await audioContext.resume();
      }

      // Don't process chunks if user paused - they'll be processed when resumed
      if (userPausedRef.current) {
        return;
      }

      // Safari fix: wait for context to be running before processing
      if (audioContext.state !== "running") {
        await new Promise<void>((resolve) => {
          const handleStateChange = () => {
            if (audioContext.state === "running") {
              audioContext.removeEventListener(
                "statechange",
                handleStateChange
              );
              resolve();
            }
          };
          audioContext.addEventListener("statechange", handleStateChange);
          // Fallback timeout in case statechange never fires
          setTimeout(() => {
            audioContext.removeEventListener("statechange", handleStateChange);
            resolve();
          }, 500);
        });
      }

      if (!isPlayingRef.current) {
        isPlayingRef.current = true;
        nextPlayTimeRef.current = audioContext.currentTime;
      }

      processPendingChunks();
    },
    [
      processPendingChunks,
      reset,
      base64ToArrayBuffer,
      pcmToFloat32,
      getAudioContext,
      onPlaybackComplete,
    ]
  );

  // Safari fix: prepare AudioContext on user gesture before chunks arrive
  const prepare = useCallback(async () => {
    const audioContext = getAudioContext();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
  }, [getAudioContext]);

  return { state, addChunk, stop, reset, pause, resume, prepare };
};
